import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useUnassignedSummary } from '@/hooks/useActiveLocations';
import { formatUnitLabel } from '@/lib/unit-label';

/**
 * Story 69.8 — com 2 ou mais unidades, atendimento sem unidade conta na unidade padrão. O aviso
 * aparece só enquanto isso existe e só para quem organiza as unidades. Recolher vale até o dia
 * seguinte: enquanto houver pendência, a clínica reencontra o aviso amanhã.
 */

export const UNITS_SETUP_NOTICE_KEY = 'valiosas.unitsSetupNotice.dismissedOn';

const todayKey = () => new Date().toISOString().slice(0, 10);

function readDismissed(): boolean {
  try {
    return localStorage.getItem(UNITS_SETUP_NOTICE_KEY) === todayKey();
  } catch {
    return false;
  }
}

export function UnitsSetupNoticeBanner() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const canManage = useCanAccess('locations:manage');
  const { data: summary } = useUnassignedSummary({ enabled: canManage });
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!canManage || dismissed || !summary) return null;
  if (pathname.replace(/\/+$/, '').toLowerCase() === '/unidades/organizar') return null;
  // Mora no SHELL: payload fora do formato vira "nada a avisar", nunca tela branca.
  const pending = Number(summary.appointmentsWithoutUnit) || 0;
  if ((summary.activeCount ?? 0) < 2 || (pending === 0 && summary.defaultLocation)) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(UNITS_SETUP_NOTICE_KEY, todayKey());
    } catch {
      // Sem armazenamento, o aviso só some até recarregar.
    }
    setDismissed(true);
  };

  // As unidades costumam ter o mesmo nome; o que diferencia é a cidade.
  const unit = summary.defaultLocation ? formatUnitLabel(summary.defaultLocation) : '';
  const title = !summary.defaultLocation
    ? 'A clínica ainda não tem unidade padrão.'
    : pending === 1
      ? `1 atendimento está sem unidade e conta na ${unit}.`
      : `${pending} atendimentos estão sem unidade e contam na ${unit}.`;

  return (
    <div className="flex flex-wrap items-start gap-3 border-b border-sky-200 bg-sky-50 px-4 py-3 text-so-14 text-sky-950 lg:px-8">
      <MapPin className="mt-0.5 size-4 shrink-0 text-sky-700" />
      <div className="min-w-0 flex-1">
        <div className="font-bold">{title}</div>
        <p className="mt-1 text-sky-900">
          Diga onde foi cada um para o resultado de cada unidade ficar certo. Dá para fazer vários de uma vez.
        </p>
        <div className="mt-2">
          <button
            type="button"
            onClick={() => navigate('/unidades/organizar')}
            // min-h-11 = 44px: no celular este é um alvo de dedo, não de mouse.
            className="min-h-11 rounded-so-sm bg-sky-900 px-3 py-1.5 text-so-12 font-bold text-white hover:bg-sky-800 sm:min-h-0"
          >
            Organizar unidades
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Recolher aviso"
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-so-sm px-2 text-so-12 font-bold text-sky-700 hover:bg-sky-100"
      >
        <X className="size-4" />
        Recolher
      </button>
    </div>
  );
}
